/**
 * A2 — detail-page refresh for freshly completed deliveries
 * (docs/incidents/2026-09-24-sunday-fixes-validation.md §4/§5).
 *
 * Two facts from Sunday 09-20 drive this:
 *   - 23 % of deliveries are first opened within 60 s of completion, while the
 *     driver's phone is often still uploading photos (CloudFront answers 500
 *     for a photo that isn't there yet, p50 4 s / p90 13 s after completion).
 *     The page never retried a failed image, so 2–6 % of opens showed a broken
 *     photo until a manual reload.
 *   - the final model result lands ~67 s after completion (Onfleet sends one
 *     more update ~60 s later for half of all deliveries), and with several
 *     autoscale copies the live `evidence_updated` reaches a given tab only
 *     when its socket sits on the copy that ran the pipeline. The page did
 *     not poll its own delivery (staleTime: Infinity), so most early openers
 *     never saw the final result without navigating away.
 *
 * Pure helpers here (unit-tested); React hooks below.
 */
import { useEffect, useState, useCallback, useRef } from "react";

/** Retry a failed photo after 3 s, 10 s and 30 s, then give up. */
export const PHOTO_RETRY_DELAYS_MS = [3_000, 10_000, 30_000] as const;

/** Poll the delivery every 15 s while it is fresh (see isDeliveryFresh). */
export const FRESH_DELIVERY_REFETCH_MS = 15_000;
/** "Fresh" = completed less than 3 minutes ago … */
export const FRESH_DELIVERY_WINDOW_MS = 3 * 60_000;
/** … or extraction still in flight, but never past this cap (a stuck row
 * should not keep a tab polling forever). */
export const FRESH_DELIVERY_PENDING_CAP_MS = 30 * 60_000;

/** Statuses after which the pipeline will not write again on its own. */
const TERMINAL_EXTRACTION_STATUSES = new Set(["complete", "deterministic_complete", "ran_no_evidence", "error"]);

/**
 * Same photo, different URL, so the browser and CloudFront's cached error are
 * bypassed: `…/800x.png?retry=2`. attempt 0 returns the URL unchanged.
 */
export function withPhotoRetryParam(url: string, attempt: number): string {
  if (attempt <= 0) return url;
  const base = url.replace(/([?&])retry=\d+&?/, "$1").replace(/[?&]$/, "");
  return `${base}${base.includes("?") ? "&" : "?"}retry=${attempt}`;
}

/** Delay before retry number `attempt` (1-based), or null when out of retries. */
export function photoRetryDelayMs(attempt: number): number | null {
  return attempt >= 1 && attempt <= PHOTO_RETRY_DELAYS_MS.length ? PHOTO_RETRY_DELAYS_MS[attempt - 1] : null;
}

export interface FreshnessFields {
  completedAt?: string | Date | null;
  actualExtractionStatus?: string | null;
}

/**
 * Should the detail page keep refetching this delivery?
 *   - yes for 3 minutes after completion, whatever the status;
 *   - yes while extraction is not in a terminal status, up to 30 minutes
 *     after completion;
 *   - no for an active (not yet completed) task, and no once both windows
 *     have passed.
 */
export function isDeliveryFresh(d: FreshnessFields | null | undefined, nowMs: number = Date.now()): boolean {
  if (!d?.completedAt) return false;
  const completedMs = new Date(d.completedAt).getTime();
  if (!Number.isFinite(completedMs)) return false;
  const age = nowMs - completedMs;
  if (age < 0) return true; // clock skew: treat as just completed
  if (age < FRESH_DELIVERY_WINDOW_MS) return true;
  const status = d.actualExtractionStatus ?? null;
  const terminal = status != null && TERMINAL_EXTRACTION_STATUSES.has(status);
  return !terminal && age < FRESH_DELIVERY_PENDING_CAP_MS;
}

/** TanStack `refetchInterval` function for a delivery query. */
export function freshDeliveryRefetchInterval(data: FreshnessFields | null | undefined): number | false {
  return isDeliveryFresh(data) ? FRESH_DELIVERY_REFETCH_MS : false;
}

/**
 * For an <img>: returns the src to render and an onError handler that
 * re-requests the photo after 3 / 10 / 30 s with a changed URL. Resets when
 * the underlying URL changes.
 */
export function useRetryingImg(url: string): { src: string; onError: () => void } {
  const [attempt, setAttempt] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setAttempt(0);
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [url]);

  const onError = useCallback(() => {
    setAttempt((current) => {
      const next = current + 1;
      const delay = photoRetryDelayMs(next);
      if (delay == null || timer.current) return current;
      timer.current = setTimeout(() => {
        timer.current = null;
        setAttempt(next);
      }, delay);
      return current;
    });
  }, []);

  return { src: withPhotoRetryParam(url, attempt), onError };
}

/**
 * For a CSS background image (no onError available): probes the URL with an
 * Image() and, on failure, retries on the same schedule. Returns the URL to
 * put in the stylesheet.
 */
export function useProbedImageSrc(url: string | null | undefined): string | null {
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setAttempt(0);
    if (!url || typeof Image === "undefined") return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const probe = (n: number) => {
      const img = new Image();
      img.onerror = () => {
        if (cancelled) return;
        const delay = photoRetryDelayMs(n + 1);
        if (delay == null) return;
        timer = setTimeout(() => {
          if (cancelled) return;
          setAttempt(n + 1);
          probe(n + 1);
        }, delay);
      };
      img.src = withPhotoRetryParam(url, n);
    };
    probe(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [url]);

  return url ? withPhotoRetryParam(url, attempt) : null;
}
